class ProductSiblingsSlider extends HTMLElement {
    static get observedAttributes() { return ["heading"]; }

    constructor() {
      super();
      this.attachShadow({ mode: "open" });

      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);

      this._resizeObserver = null;

      this.shadowRoot.innerHTML = `
        <style>
          :host { display:block; }

          .wrap {
            display:block;
          }

          .header{
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:12px;
            margin-bottom:10px;
          }

          .name{
            font-size: inherit;
            font-weight: inherit;
          }

          .nav{
            display:flex;
            gap:8px;
            flex: 0 0 auto;
          }

          button{
            appearance:none;
            border:1px solid rgba(0,0,0,.15);
            background:#fff;
            width:36px;
            height:36px;
            border-radius:999px;
            display:inline-flex;
            align-items:center;
            justify-content:center;
            cursor:pointer;
            line-height:1;
            font-size:18px;
            padding:0;
          }

          button[disabled]{
            opacity:.35;
            cursor:not-allowed;
          }

          .viewport{
            overflow:hidden;
          }

          .name.text-size--large {
            font-weight: var(--font-weight-body-bold);
            color: var(--color-text-main);
            font-size: calc(18px / 16 * var(--base-body-size) + 0px);
          }

          .track{
            display:flex;
            gap:var(--ps-gap, 12px);
            overflow-x:auto;
            scroll-snap-type:x mandatory;
            scroll-behavior:smooth;
            -webkit-overflow-scrolling:touch;
            padding-bottom:2px;
            scrollbar-width:none;
          }

          .track::-webkit-scrollbar{ height:0; }

          /* Default: 2 visible on mobile */
          ::slotted(*) {
            flex: 0 0 calc((100% - var(--ps-gap, 12px)) / 2);
            scroll-snap-align:start;
          }

          /* 3 up to small screen */
          @media (min-width: 750px){
            ::slotted(*) {
              flex-basis: calc((100% - (var(--ps-gap, 12px) * 2)) / 3);
            }
          }

          /* 4 on big screens */
          @media (min-width: 1280px){
            ::slotted(*) {
              flex-basis: calc((100% - (var(--ps-gap, 12px) * 3)) / 4);
            }
          }
        </style>

        <div class="wrap">
          <div class="header">
            <span class="name text-size--large" part="heading"></span>
            <div class="nav">
              <button type="button" class="prev" aria-label="Previous">‹</button>
              <button type="button" class="next" aria-label="Next">›</button>
            </div>
          </div>

          <div class="viewport">
            <div class="track" part="track">
              <slot></slot>
            </div>
          </div>
        </div>
      `;
    }

    connectedCallback() {
      this._nameEl = this.shadowRoot.querySelector(".name");
      this._track = this.shadowRoot.querySelector(".track");
      this._slot = this.shadowRoot.querySelector("slot");
      this._prevBtn = this.shadowRoot.querySelector(".prev");
      this._nextBtn = this.shadowRoot.querySelector(".next");

      this._applyHeading();

      this._prevBtn.addEventListener("click", this._onPrev);
      this._nextBtn.addEventListener("click", this._onNext);
      this._track.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onResize);

      this._slot.addEventListener("slotchange", () => this._updateButtons());

      if ("ResizeObserver" in window) {
        this._resizeObserver = new ResizeObserver(() => this._updateButtons());
        this._resizeObserver.observe(this);
      }

      this._updateButtons();
    }

    disconnectedCallback() {
      this._prevBtn.removeEventListener("click", this._onPrev);
      this._nextBtn.removeEventListener("click", this._onNext);
      this._track.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onResize);

      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    attributeChangedCallback(name) {
      if (name === "heading") this._applyHeading();
    }

    _applyHeading() {
      if (!this._nameEl) return;
      const heading = this.getAttribute("heading") || "";
      this._nameEl.textContent = heading;
    }

    _getStep() {
      const items = this._slot.assignedElements({ flatten: true });
      if (!items || items.length === 0) return this._track.clientWidth;

      const first = items[0];
      const firstRect = first.getBoundingClientRect();

      let gap = 0;
      if (items.length > 1) {
        const secondRect = items[1].getBoundingClientRect();
        const rawGap = secondRect.left - firstRect.right;
        gap = rawGap > 0 ? Math.round(rawGap) : 0;
      } else {
        const computed = getComputedStyle(this._track).gap;
        const parsed = parseFloat(computed);
        gap = Number.isFinite(parsed) ? parsed : 0;
      }

      return Math.round(firstRect.width + gap);
    }

    _maxScrollLeft() {
      return Math.max(0, this._track.scrollWidth - this._track.clientWidth);
    }

    _updateButtons() {
      const max = Math.round(this._maxScrollLeft());
      const left = Math.round(this._track.scrollLeft);

      this._prevBtn.disabled = left <= 0;
      this._nextBtn.disabled = left >= max;
    }

    _scrollBy(dir) {
      const step = this._getStep();
      this._track.scrollBy({ left: step * dir, behavior: "smooth" });
      window.setTimeout(() => this._updateButtons(), 150);
    }

    _onPrev() { this._scrollBy(-1); }
    _onNext() { this._scrollBy(1); }
    _onScroll() { this._updateButtons(); }
    _onResize() { this._updateButtons(); }
  }

  if (!customElements.get("product-siblings-slider")) {
    customElements.define("product-siblings-slider", ProductSiblingsSlider);
  }